import { useQuery } from '@tanstack/react-query';
import { Alert, Button, ButtonGroup, Col, Container, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  LeaderRatingsAggregationsService,
  LeaderRatingsService,
  PeerRatingsAggregationsService,
  PeerRatingsService,
  ProviderService,
} from '../../../api';
import { Card } from '../../../app/components/Card';
import { ScaleSelectRatingDisplay } from '../../../app/components/ScaleSelectRatingDisplay';
import { StarRatingDisplay } from '../../../app/components/StarRatingDisplay';
import { useSearchParamsState } from '../../../app/hooks/search-params';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { leaderRatingsKeys } from '../../../app/query-keys-factories/leader-ratings';
import { peerRatingsKeys } from '../../../app/query-keys-factories/peer-ratings';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';

export const Reputation = () => {
  useDocumentTitle('Provider Reputation');
  const [feedbackType, setFeedbackType] = useSearchParamsState('feedback_type', 'PEER');
  const [page, setPage] = useSearchParamsState('page', '1');

  const {
    isLoading: isProviderLoading,
    error: providerFetchError,
    data: provider,
  } = useQuery({
    queryKey: providerQueryKeys.currentDetail(),
    queryFn: async () => await ProviderService.providerCurrentRetrieve(),
  });

  const {
    isLoading: isPeerRatingsAggregationLoading,
    error: peerRatingsAggregationFetchError,
    data: peerRatingsAggregation,
  } = useQuery({
    queryKey: peerRatingsKeys.detail(provider?.id ?? ''),
    queryFn: async () =>
      await PeerRatingsAggregationsService.peerRatingsAggregationsRetrieve({
        providerId: provider!.id,
      }),
    enabled: !!provider?.id && feedbackType === 'PEER',
  });

  const {
    isLoading: isLeaderRatingsAggregationLoading,
    error: leaderRatingsAggregationFetchError,
    data: leaderRatingsAggregation,
  } = useQuery({
    queryKey: leaderRatingsKeys.detail(provider?.id as string),
    queryFn: async () =>
      await LeaderRatingsAggregationsService.leaderRatingsAggregationsRetrieve({
        providerId: provider?.id as string,
      }),
    enabled: !!provider?.id && feedbackType === 'LEADER',
  });

  const limit = 10;
  const offset = (parseInt(page) - 1) * limit;
  const {
    isLoading: isLeaderRatingsLoading,
    error: leaderRatingsFetchError,
    data: leaderRatings,
  } = useQuery({
    queryKey: leaderRatingsKeys.list({ limit, offset }),
    queryFn: async () => await LeaderRatingsService.leaderRatingsList({ limit, offset }),
    enabled: feedbackType === 'LEADER',
  });
  const {
    isLoading: isPeerRatingsLoading,
    error: peerRatingsFetchError,
    data: peerRatings,
  } = useQuery({
    queryKey: peerRatingsKeys.list({ limit, offset }),
    queryFn: async () => await PeerRatingsService.peerRatingsList({ limit, offset }),
    enabled: feedbackType === 'PEER',
  });

  const totalCount = feedbackType === 'PEER' ? peerRatings?.count : leaderRatings?.count;

  return (
    <Container>
      {isProviderLoading && (
        <Row>
          <Col xs={12} className="d-flex align-items-center justify-content-center">
            <Spinner animation="grow" className="loading-spinner" variant="primary" />
          </Col>
        </Row>
      )}
      {!!providerFetchError && (
        <Row className="row-cols-auto justify-content-center">
          <Col>
            <Alert variant="danger" show={!!providerFetchError}>
              <Alert.Heading>Something went wrong</Alert.Heading>
              <p>Please try again later. If the problem persists, please contact us.</p>
            </Alert>
          </Col>
        </Row>
      )}
      {!!provider && (
        <>
          <div className="d-flex flex-wrap align-items-start">
            {provider && (
              <header className="flex-grow-1">
                <h2>My Rating - {feedbackType === 'PEER' ? 'Peer' : 'Leader'} Feedback</h2>
                {feedbackType === 'PEER' && (
                  <small>
                    Want more reviews? Copy your{' '}
                    <Link to={`/app/peer-rating/${provider.id}`}>Peer Feedback link</Link> and share
                    with your peers
                  </small>
                )}
              </header>
            )}
            <ButtonGroup className="mb-3 me-3">
              <Button
                variant={feedbackType === 'PEER' ? 'primary' : 'outline-primary'}
                onClick={() => {
                  setPage('1');
                  setFeedbackType('PEER');
                }}
              >
                Peer Feedback
              </Button>
              <Button
                variant={feedbackType === 'LEADER' ? 'primary' : 'outline-primary'}
                onClick={() => {
                  setPage('1');
                  setFeedbackType('LEADER');
                }}
              >
                Leader Feedback
              </Button>
            </ButtonGroup>
          </div>

          {(!totalCount || totalCount === 0) && (
            <p className="my-3 text-center">No ratings received yet</p>
          )}

          {feedbackType === 'LEADER' && !!totalCount && totalCount > 0 && (
            <>
              <Row>
                <Col>
                  <Card.SubCard className="p-4 my-4">
                    <h5>Average Ratings</h5>
                    {isLeaderRatingsAggregationLoading && (
                      <Row>
                        <Col xs={12} className="d-flex align-items-center justify-content-center">
                          <Spinner animation="grow" className="loading-spinner" variant="primary" />
                        </Col>
                      </Row>
                    )}
                    {!!leaderRatingsAggregationFetchError && (
                      <Row className="row-cols-auto justify-content-center">
                        <Col>
                          <Alert variant="danger" show={!!providerFetchError}>
                            <Alert.Heading>Something went wrong</Alert.Heading>
                            <p>
                              Please try again later. If the problem persists, please contact us.
                            </p>
                          </Alert>
                        </Col>
                      </Row>
                    )}
                    {leaderRatingsAggregation && (
                      <Row>
                        <Col xs={12} lg={12}>
                          <Card.Info label="Shifts Performance">
                            <ScaleSelectRatingDisplay
                              rating={leaderRatingsAggregation?.leader_shifts_performance_rating}
                              ratingsCount={
                                leaderRatingsAggregation?.leader_shifts_performance_ratings_count
                              }
                            />
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={3}>
                          <Card.Info label="General">
                            <StarRatingDisplay
                              compact
                              rating={leaderRatingsAggregation?.leader_general_rating}
                              ratingsCount={
                                leaderRatingsAggregation?.leader_patient_closecase_ratings_count
                              }
                            />
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={3}>
                          <Card.Info label="Clinical Competency">
                            <StarRatingDisplay
                              compact
                              rating={leaderRatingsAggregation?.leader_clinical_competency_rating}
                              ratingsCount={leaderRatingsAggregation?.leader_general_ratings_count}
                            />
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={3}>
                          <Card.Info label="Communication & Teamwork">
                            <StarRatingDisplay
                              compact
                              rating={
                                leaderRatingsAggregation?.leader_communication_and_teamwork_rating
                              }
                              ratingsCount={
                                leaderRatingsAggregation?.leader_clinical_competency_ratings_count
                              }
                            />
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={3}>
                          <Card.Info label="Patient Closecase">
                            <StarRatingDisplay
                              compact
                              rating={leaderRatingsAggregation?.leader_patient_closecase_rating}
                              ratingsCount={
                                leaderRatingsAggregation?.leader_communication_and_teamwork_ratings_count
                              }
                            />
                          </Card.Info>
                        </Col>
                      </Row>
                    )}
                  </Card.SubCard>
                </Col>
              </Row>
              {isLeaderRatingsLoading && (
                <Row>
                  <Col xs={12} className="d-flex align-items-center justify-content-center">
                    <Spinner animation="grow" className="loading-spinner" variant="primary" />
                  </Col>
                </Row>
              )}
              {!!leaderRatingsFetchError && (
                <Row className="row-cols-auto justify-content-center">
                  <Col>
                    <Alert variant="danger" show={!!providerFetchError}>
                      <Alert.Heading>Something went wrong</Alert.Heading>
                      <p>Please try again later. If the problem persists, please contact us.</p>
                    </Alert>
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  {leaderRatings?.results?.map((rating) => (
                    <Card.SubCard key={rating.id} className="p-4 mb-4">
                      <h5>Review by Facility {rating.healthcare_facility_name}</h5>
                      <Row>
                        <Col xs={12} lg={3}>
                          <Card.Info label="Shifts Performance">
                            {!!rating?.shifts_performance_rating && (
                              <ScaleSelectRatingDisplay
                                rating={rating?.shifts_performance_rating}
                              />
                            )}
                          </Card.Info>
                          <Card.Info label="General">
                            {!!rating?.general_rating && (
                              <StarRatingDisplay compact rating={rating?.general_rating} />
                            )}
                          </Card.Info>
                          <Card.Info label="Clinical Competency">
                            {!!rating?.clinical_competency_rating && (
                              <StarRatingDisplay
                                compact
                                rating={rating?.clinical_competency_rating}
                              />
                            )}
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={3}>
                          <Card.Info label="Communication & Teamwork">
                            {!!rating?.communication_and_teamwork_rating && (
                              <StarRatingDisplay
                                compact
                                rating={rating?.communication_and_teamwork_rating}
                              />
                            )}
                          </Card.Info>
                          <Card.Info label="Patient Closecase">
                            {!!rating?.patient_closecase_rating && (
                              <StarRatingDisplay
                                compact
                                rating={rating?.patient_closecase_rating}
                              />
                            )}
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={6}>
                          <Card.Info label="Comment">
                            <p className="fst-italic">{rating.comment ? rating.comment : 'None'}</p>
                          </Card.Info>
                        </Col>
                      </Row>
                    </Card.SubCard>
                  ))}
                </Col>
              </Row>
            </>
          )}

          {feedbackType === 'PEER' && !!totalCount && totalCount > 0 && (
            <>
              <Row>
                <Col>
                  <Card.SubCard className="p-4 my-4">
                    <h5>Average Ratings</h5>
                    {isPeerRatingsAggregationLoading && (
                      <Row>
                        <Col xs={12} className="d-flex align-items-center justify-content-center">
                          <Spinner animation="grow" className="loading-spinner" variant="primary" />
                        </Col>
                      </Row>
                    )}
                    {!!peerRatingsAggregationFetchError && (
                      <Row className="row-cols-auto justify-content-center">
                        <Col>
                          <Alert variant="danger" show={!!providerFetchError}>
                            <Alert.Heading>Something went wrong</Alert.Heading>
                            <p>
                              Please try again later. If the problem persists, please contact us.
                            </p>
                          </Alert>
                        </Col>
                      </Row>
                    )}
                    {peerRatingsAggregation && (
                      <Row>
                        <Col xs={12} lg={3}>
                          <Card.Info label="General">
                            <StarRatingDisplay
                              compact
                              rating={peerRatingsAggregation?.peer_general_rating}
                              ratingsCount={
                                peerRatingsAggregation?.peer_patient_closecase_ratings_count
                              }
                            />
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={3}>
                          <Card.Info label="Clinical Competency">
                            <StarRatingDisplay
                              compact
                              rating={peerRatingsAggregation?.peer_clinical_competency_rating}
                              ratingsCount={peerRatingsAggregation?.peer_general_ratings_count}
                            />
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={3}>
                          <Card.Info label="Communication & Teamwork">
                            <StarRatingDisplay
                              compact
                              rating={
                                peerRatingsAggregation?.peer_communication_and_teamwork_rating
                              }
                              ratingsCount={
                                peerRatingsAggregation?.peer_clinical_competency_ratings_count
                              }
                            />
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={3}>
                          <Card.Info label="Patient Closecase">
                            <StarRatingDisplay
                              compact
                              rating={peerRatingsAggregation?.peer_patient_closecase_rating}
                              ratingsCount={
                                peerRatingsAggregation?.peer_communication_and_teamwork_ratings_count
                              }
                            />
                          </Card.Info>
                        </Col>
                      </Row>
                    )}
                  </Card.SubCard>
                </Col>
              </Row>
              {isPeerRatingsLoading && (
                <Row>
                  <Col xs={12} className="d-flex align-items-center justify-content-center">
                    <Spinner animation="grow" className="loading-spinner" variant="primary" />
                  </Col>
                </Row>
              )}
              {!!peerRatingsFetchError && (
                <Row className="row-cols-auto justify-content-center">
                  <Col>
                    <Alert variant="danger" show={!!providerFetchError}>
                      <Alert.Heading>Something went wrong</Alert.Heading>
                      <p>Please try again later. If the problem persists, please contact us.</p>
                    </Alert>
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  {peerRatings?.results?.map((rating) => (
                    <Card.SubCard key={rating.id} className="p-4 mb-4">
                      <h5>Someone that worked alongside with you</h5>
                      <Row>
                        <Col xs={12} lg={3}>
                          <Card.Info label="General">
                            {!!rating?.general_rating && (
                              <StarRatingDisplay compact rating={rating?.general_rating} />
                            )}
                          </Card.Info>
                          <Card.Info label="Clinical Competency">
                            {!!rating?.clinical_competency_rating && (
                              <StarRatingDisplay
                                compact
                                rating={rating?.clinical_competency_rating}
                              />
                            )}
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={3}>
                          <Card.Info label="Communication & Teamwork">
                            {!!rating?.communication_and_teamwork_rating && (
                              <StarRatingDisplay
                                compact
                                rating={rating?.communication_and_teamwork_rating}
                              />
                            )}
                          </Card.Info>
                          <Card.Info label="Patient Closecase">
                            {!!rating?.patient_closecase_rating && (
                              <StarRatingDisplay
                                compact
                                rating={rating?.patient_closecase_rating}
                              />
                            )}
                          </Card.Info>
                        </Col>
                        <Col xs={12} lg={6}>
                          <Card.Info label="Comment">
                            <p className="fst-italic">{rating.comment ? rating.comment : 'None'}</p>
                          </Card.Info>
                        </Col>
                      </Row>
                    </Card.SubCard>
                  ))}
                </Col>
              </Row>
            </>
          )}

          <div className="d-flex justify-content-stretch mt-5">
            {page !== '1' && (
              <div className="d-flex flex-grow-1 justify-content-start">
                <Button onClick={() => setPage((parseInt(page) - 1).toString())}>
                  {' '}
                  Previous Page
                </Button>
              </div>
            )}

            {totalCount !== undefined &&
              totalCount > 0 &&
              parseInt(page) < Math.ceil(totalCount / limit) && (
                <div className="d-flex flex-grow-1 justify-content-end">
                  <Button onClick={() => setPage((parseInt(page) + 1).toString())}>
                    Next Page
                  </Button>
                </div>
              )}
          </div>
        </>
      )}
    </Container>
  );
};
